// Entry point.
//
// Boot order matters here and is not arbitrary:
//   1. `boot()` is kicked off BEFORE the router renders. It reads the session
//      mirror out of IndexedDB and does not await the network, so it settles
//      in milliseconds even in airplane mode. Route guards await
//      `waitForSessionBoot()` rather than racing it.
//   2. The router renders immediately; it does not wait for the network either.
//   3. A subscription re-runs the guards if the session is ever revoked while
//      a protected screen is on-screen.
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { DatabaseResetScreen } from '@/components/database-reset-screen';
import { ErrorScreen } from '@/components/error-screen';
import { startSessionWatchers, useSessionStore } from '@/lib/auth/session';
import { checkDatabaseOpens } from '@/lib/db-open-guard';
import { startOffline } from '@/lib/offline';
import { APP_MAIN_SCROLL_ID } from '@/lib/use-restore-list-scroll';

import { registerServiceWorker } from './pwa/register';
import { routeTree } from './routeTree.gen';

import './index.css';

const router = createRouter({
  routeTree,
  // A crash in any route renders this instead of TanStack Router's bare
  // default (which offers no way out) — the user can reload or return to the
  // board rather than being stranded on a blank screen.
  defaultErrorComponent: ErrorScreen,
  // Preload on hover/touch-start. At a booth this hides most of the latency
  // between tapping a lead and seeing it.
  defaultPreload: 'intent',
  scrollRestoration: true,
  // The shell's <main> is the only scrolling element in the app, so it is the
  // one every screen shares. Without this the router COPIES its scroll offset
  // onto each newly pushed history entry — sensible for a persistent side
  // panel, wrong here: tapping Products in the tab bar would open the catalogue
  // wherever you last left it. Listing it makes a push scroll it to top, while
  // a back-navigation, which has a real cached entry for its key, still
  // restores (the restore loop removes a restored element from this set).
  scrollToTopSelectors: [`[data-scroll-restoration-id="${APP_MAIN_SCROLL_ID}"]`]
});

// One client for the whole app.
//
// `networkMode: 'always'` is the load-bearing setting, and it is a deliberate,
// global change to offline semantics (docs/offline-hardening-todo.md #3).
// TanStack's default 'online' mode checks `navigator.onLine` and, when false,
// NEVER CALLS `queryFn` — the query parks at `fetchStatus: 'paused'`. In this
// app that is a correctness bug, not a nicety: every fetcher goes through
// `apiRequest`, which classifies its own outcome (`ok | unauthenticated |
// error | offline`) and never throws, and the offline fallbacks hang off that
// classification — the leads mirror behind lead detail, the event-tag mirror
// behind the product picker, the "You are offline" copy on the print screens.
// Paused, none of it runs and the screen sits on "Loading…" forever. Measured
// in airplane mode: lead detail issued ZERO `/api/leads/:id` requests.
//
// It only bit in TRUE airplane mode — on flaky or captive-portal wifi
// `navigator.onLine` stays true, the query fires, and everything classifies
// correctly. Which is exactly why manual testing never caught it.
//
// `refetchOnReconnect` is set EXPLICITLY because 'always' flips its default to
// `false` (documented TanStack behaviour). Leaving it defaulted would trade a
// hang for staleness: a screen that resolved from the local mirror in a dead
// zone would keep showing mirror data after the signal came back.
//
// Mutations get the same mode for the same reason. A paused mutation is
// in-memory only — it dies on reload and leaves the UI spinning with no error.
// Durable mutations belong in the Dexie outbox (lib/offline/outbox.ts); the
// ones that reach TanStack (archive, restore, delete, event tagging) are
// documented online-only and should fail loudly, not silently queue.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { networkMode: 'always', refetchOnReconnect: true },
    mutations: { networkMode: 'always' }
  }
});

// Module augmentation must see the concrete router instance, which is why the
// router is built at module scope rather than inside the branch below.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element #root not found');
}

const root = createRoot(container);

// A revoked session must not leave the user sitting on a protected screen.
// `applySessionResult` only reaches 'unauthenticated' for a real 401/403 (or
// Better Auth's null-body equivalent) — never for a network failure — so
// this cannot fire simply because someone walked into a dead zone.
useSessionStore.subscribe((state, previous) => {
  if (previous.status === 'authenticated' && state.status === 'unauthenticated') {
    void router.invalidate();
  }
});

startSessionWatchers();
void useSessionStore.getState().boot();

// The offline core: outbox counter into the logout guard, sync engine, and the
// initial catalogue / buyer-directory / leads-mirror pulls. Kicked off after
// boot() (which does not block on the network) so a cold start in airplane mode
// still renders instantly from the Dexie mirror.
startOffline();

// Service worker registration is a no-op in dev (Vite/HMR owns that); in
// production it enables the offline shell + image cache. Registered last so a
// failure here can never delay first paint.
registerServiceWorker();

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>
);

// Checked AFTER the first render, not before it: the normal path must not wait
// on IndexedDB to paint, and a version mismatch is deterministic and immediate,
// so replacing the tree costs nothing in the case that actually happens.
void checkDatabaseOpens().then(failure => {
  if (failure !== null) root.render(<DatabaseResetScreen />);
});
